import {
  BodyText,
  Card,
  CardContent,
  HeadingText,
  NextAlert,
  NextButton,
  NextSwitch,
  NextTooltipContent,
  Spinner,
  Tooltip,
  TooltipIcon,
  TooltipProvider,
  TooltipTrigger,
} from '@cint/wonderment';
import { useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';

import { FileUploadInput, NavigateAwayDialog } from '@/components';
import { useImportNavigationGuard } from '@/hooks';
import { MAX_UPLOAD_FILE_SIZE_MB } from '@/pages/PanelistImport/constants';

interface Props {
  importToProcessId: string | null;
  errorFileId?: string | null;
  errorCount: number | null;
  isUploading: boolean;
  linesCount: number;
  onDrop: (acceptedFiles: any, rejectedFiles: any) => void;
  handleDownloadTemplate: () => void;
  handleDownloadErrorFile?: (id: string) => void;
  setIsConfirmDialogOpen: (open: boolean) => void;
  setIsDeleteDialogOpen: (open: boolean) => void;
  fileName: string;
  isProcessingLoading?: boolean;
  onDownloadErrors?: () => void;
  areFieldsVisibleDuringRegistration?: boolean;
  onToggleAreFieldsVisibleDuringRegistration?: (value: boolean) => void;
  entityName?: string;
  requiredFields?: string[];
}

export const FileUploadSection = ({
  importToProcessId,
  errorFileId,
  errorCount,
  isUploading,
  linesCount,
  onDrop,
  handleDownloadTemplate,
  handleDownloadErrorFile,
  setIsConfirmDialogOpen,
  setIsDeleteDialogOpen,
  fileName,
  isProcessingLoading = false,
  onDownloadErrors,
  areFieldsVisibleDuringRegistration = false,
  onToggleAreFieldsVisibleDuringRegistration,
  entityName = 'panelists',
  requiredFields = ['Email', 'Birth Date', 'Gender', 'Postal code'],
}: Props) => {
  const { open: openBrowseFiles, getInputProps } = useDropzone({
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.csv'],
      'text/plain': ['.csv'],
    },
    noClick: true,
    maxFiles: 1,
    onDrop,
    multiple: false,
  });

  const formattedDate = useMemo(
    () =>
      new Date().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }),
    []
  );

  const requiredFieldsText = useMemo(
    () =>
      requiredFields?.length
        ? `Maximum file size: ${MAX_UPLOAD_FILE_SIZE_MB}MB. Supported format: CSV only. Required fields: ${requiredFields.join(
            ', '
          )}.`
        : '',
    [requiredFields]
  );

  const { isDialogOpen, onLeave, onStay } = useImportNavigationGuard({
    enabled: Boolean(fileName),
  });

  const hasErrors = Boolean(errorCount && errorCount > 0);
  const isLeadsMode = Boolean(onToggleAreFieldsVisibleDuringRegistration);
  const showFile = Boolean(fileName && !isUploading);
  const showActions = Boolean(
    (importToProcessId || hasErrors) && !isUploading && !isProcessingLoading
  );

  const handleErrorDownload = useCallback(() => {
    if (onDownloadErrors) {
      onDownloadErrors();
    } else if (handleDownloadErrorFile && errorFileId) {
      handleDownloadErrorFile(errorFileId);
    }
  }, [onDownloadErrors, handleDownloadErrorFile, errorFileId]);

  const renderFieldVisibilityToggle = () => (
    <div className="flex items-center gap-2 mt-4">
      <div className="flex items-center">
        <NextSwitch
          isChecked={areFieldsVisibleDuringRegistration}
          onClick={onToggleAreFieldsVisibleDuringRegistration}
          size="base"
        />
        <div className="ml-2 flex items-center gap-1">
          <BodyText fontWeight="medium" size="sm">
            Field visibility during registration
          </BodyText>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <TooltipIcon />
              </TooltipTrigger>
              <NextTooltipContent size="lg">
                <BodyText size="sm">
                  Choose whether to show or hide validated fields during
                  sign-up.
                </BodyText>
                <ul className="list-disc pl-5 mt-2">
                  <li>
                    <BodyText size="sm">
                      Hide fields to streamline sign-up if you trust your
                      existing data.
                    </BodyText>
                  </li>
                  <li>
                    <BodyText size="sm">
                      Show fields to let users review and update their
                      information.
                    </BodyText>
                  </li>
                </ul>
              </NextTooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
    </div>
  );

  const renderFileInfo = () => (
    <Card className="mb-6 min-h-50">
      <CardContent className="size-full">
        <div>
          <div
            className={
              isLeadsMode
                ? 'flex flex-col'
                : 'flex items-center justify-between'
            }
          >
            <div>
              <div className="flex items-center gap-2">
                <BodyText fontWeight="medium" className="mb-1">
                  {fileName}
                </BodyText>
              </div>
              <BodyText fontWeight="normal" size="xs">
                {formattedDate}
              </BodyText>
            </div>

            {isLeadsMode && !hasErrors && renderFieldVisibilityToggle()}
            {isProcessingLoading && <Spinner />}
          </div>

          {hasErrors && (
            <NextAlert
              className="mt-4"
              actionButtonProps={{
                onClick: handleErrorDownload,
                text: 'Download error report',
              }}
              description="Please correct the fields and upload the fixed file."
              iconName="Info"
              title={`This file contains ${errorCount} rows with errors and can not be imported.`}
              variant="danger"
            />
          )}
        </div>
      </CardContent>
    </Card>
  );

  const renderActionButtons = () => (
    <div className="flex justify-end gap-2">
      <NextButton
        size="base"
        text="Remove file"
        variant="subtle"
        onClick={() => setIsDeleteDialogOpen(true)}
      />

      {!hasErrors && (
        <NextButton
          size="base"
          text={`Import ${linesCount} ${entityName}`}
          variant="primary"
          onClick={() => setIsConfirmDialogOpen(true)}
        />
      )}

      {hasErrors && (
        <div>
          <input data-testid="file-input" {...getInputProps()} />
          <NextButton
            size="base"
            text="Replace with fixed file"
            iconProps={{ name: 'ArrowCounterClockwise' }}
            variant="primary"
            onClick={openBrowseFiles}
          />
        </div>
      )}
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <HeadingText fontWeight="normal" variant="h5">
          Upload file
        </HeadingText>
        <NextButton
          alignment="center"
          iconProps={{ name: 'DownloadSimple' }}
          size="base"
          text="Download CSV template"
          variant="link"
          onClick={handleDownloadTemplate}
        />
      </div>

      <BodyText className="ds-text-secondary mb-6">
        {requiredFieldsText}
      </BodyText>

      {(!fileName || isUploading) && (
        <FileUploadInput isUploading={isUploading} onDrop={onDrop} />
      )}

      {showFile && renderFileInfo()}

      {showActions && renderActionButtons()}

      <NavigateAwayDialog
        isOpen={isDialogOpen}
        onLeave={onLeave}
        onStay={onStay}
      />
    </div>
  );
};
